import { ReloadButton } from '@aibees/shared/components';
import { useBrowsePaymentCardApi } from '@aibees/shared/features/billing-profile/api/use-browse-payment-card-api';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { classnames } from '@aibees/shared/utils';
import { Box, Card, CardContent, Stack } from '@mui/material';
import { memo, useMemo } from 'react';
import { DefaultCardButton } from '../default-card-button';
import { NoPaymentCard } from '../no-payment-card';
import { PaymentCardInfo } from '../payment-card-info';
import { PaymentFailedAlert } from '../payment-failed-alert';
import { PaymentMethodBadges } from '../payment-method-badges';
import { PaymentMethodHeader } from '../payment-method-header';
import { RemovePaymentCard } from '../remove-payment-card';
import * as SX from './sx';
import type { PaymentMethodsProps as Props } from './types';

export const PaymentMethods = memo(({ className }: Props) => {
  const { data, isError, refetch } = useBrowsePaymentCardApi();
  const { data: subscriptionData } = useCurrentSubscriptionApi();

  const isPaidPlan = !subscriptionData?.current?.plan?.metadata?.is_free;

  const cards = data?.content;
  const hasAnyDefault = useMemo(
    () => !!cards?.some((c) => c.isDefault),
    [cards]
  );

  return (
    <Stack
      className={classnames('PaymentMethods', className)}
      direction="column"
      gap={1.5}
    >
      <PaymentFailedAlert />

      <Card headerDivider>
        <PaymentMethodHeader />
        <CardContent padding="normal">
          <Stack direction="column" gap={1}>
            {cards?.length ? (
              cards.map((x) => {
                const inUse = isPaidPlan && !!x.inUse;
                const isDefault = !!x.isDefault;

                return (
                  <Box key={x.id} sx={SX.paymentCard({ inUse, isDefault })}>
                    <PaymentCardInfo
                      item={x.card}
                      isPaymentMethod
                      iconType={x.iconType}
                    />
                    <Stack direction="row" alignItems="center" gap={1}>
                      <PaymentMethodBadges
                        inUse={inUse}
                        isDefault={isDefault}
                      />
                      <DefaultCardButton item={x} isPaidPlan={isPaidPlan} />
                      <RemovePaymentCard
                        item={x}
                        isPaidPlan={isPaidPlan}
                        hasAnyDefault={hasAnyDefault}
                      />
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <NoPaymentCard />
            )}
            {isError && <ReloadButton onReload={refetch} />}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

PaymentMethods.displayName = 'PaymentMethods';
